import { Link, usePage } from "@inertiajs/react"
import { useState } from "react"
import {
  Home,
  BookOpen,
  Heart,
  MessageCircle,
  Scale,
  Target,
  Compass,
  GraduationCap,
  MoreHorizontal,
  LogOut,
  Shield,
  Users,
  User,
  ChevronRight,
  Lock,
} from "lucide-react"
import { useMediaQuery } from "@/hooks/use-media-query"
import { ImpersonationBanner } from "@/components/ImpersonationBanner"
import { inertiaUrlPathname, inertiaUrlTab } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  ResponsiveDialogTrigger,
} from "@/components/ui/responsive-dialog"
import logoBlackText from "@/assets/logos/kf-logo-black-text.webp"
import { GroupSwitcher } from "./GroupSwitcher"
import { ViewAsMemberBanner } from "./ViewAsMemberBanner"

const ICON_MAP = {
  home: Home,
  "book-open": BookOpen,
  heart: Heart,
  "message-circle": MessageCircle,
  scale: Scale,
  target: Target,
  compass: Compass,
  "graduation-cap": GraduationCap,
  users: Users,
}

/** Sidebar + mobile "More": sub-link active under Home (query tab or wheel paths). */
function memberSubLinkIsActive(fullUrl, sub) {
  const subPath = inertiaUrlPathname(sub.href)
  const path = inertiaUrlPathname(fullUrl)
  if (subPath !== "/app/member") {
    return path === subPath || path.startsWith(`${subPath}/`)
  }
  if (path !== "/app/member") return false
  const subTab = inertiaUrlTab(sub.href)
  const curTab = inertiaUrlTab(fullUrl)
  if (subTab === "assessment") return curTab === "assessment"
  if (subTab === "plan") return curTab !== "assessment"
  return true
}

function memberTabIsActive(tab, fullUrl) {
  if (tab.subItems?.length) {
    return tab.subItems.some((sub) => memberSubLinkIsActive(fullUrl, sub))
  }
  const base = inertiaUrlPathname(tab.href)
  const path = inertiaUrlPathname(fullUrl)
  if (base === "/app/member") {
    return path === "/app/member" && !path.startsWith("/app/member/balance_wheel")
  }
  return path === base || path.startsWith(`${base}/`)
}

export function MemberLayout({ children, currentUser }) {
  const { url, props } = usePage()
  const { currentGroup, userGroups, visibleTabs } = props
  const isDesktop = useMediaQuery("(min-width: 1024px)")

  const allTabs = (visibleTabs || []).map((tab) => ({
    ...tab,
    Icon: ICON_MAP[tab.icon] || Home,
  }))

  const mobileBarTabs = allTabs.filter((tab) => tab.mobileBar)
  const mobileMoreTabs = allTabs.filter((tab) => !tab.mobileBar)

  const isActive = (tab) => memberTabIsActive(tab, url)

  const homePlanningLinks = mobileBarTabs.find((t) => t.subItems?.length)?.subItems ?? []
  const isMoreActive =
    mobileMoreTabs.some((tab) => isActive(tab)) ||
    homePlanningLinks.some((sub) => memberSubLinkIsActive(url, sub))

  // Rendered outside both layout branches so an impersonated session keeps its
  // way back on desktop and mobile alike.
  const banner = <ImpersonationBanner />

  if (isDesktop) {
    return (
      <>
        {banner}
        <DesktopLayout tabs={allTabs} isActive={isActive} url={url} currentUser={currentUser} currentGroup={currentGroup} userGroups={userGroups}>{children}</DesktopLayout>
      </>
    )
  }

  return (
    <>
      {banner}
      <MobileLayout barTabs={mobileBarTabs} moreTabs={mobileMoreTabs} homePlanningLinks={homePlanningLinks} isActive={isActive} isMoreActive={isMoreActive} url={url} currentUser={currentUser} currentGroup={currentGroup} userGroups={userGroups}>{children}</MobileLayout>
    </>
  )
}

function DesktopLayout({ children, tabs, isActive, url, currentUser, currentGroup, userGroups }) {
  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Sidebar */}
      <aside className="fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-zinc-200">
        <div className="flex h-full flex-col">
          {/* Logo */}
          <div className="flex h-16 items-center px-4 border-b border-zinc-200">
            <Link href="/" className="flex items-center gap-2">
              <img src={logoBlackText} alt="Kingdom Factor" className="h-8" />
            </Link>
          </div>

          {/* Group context */}
          <div className="px-4 py-3 border-b border-zinc-200">
            {userGroups?.length > 1 ? (
              <GroupSwitcher currentGroup={currentGroup} userGroups={userGroups} className="w-full" />
            ) : (
              <p className="text-sm font-medium text-zinc-900 truncate">
                {currentGroup?.name}
              </p>
            )}
            <p className="text-xs text-zinc-500 mt-0.5">
              {currentUser?.name || currentUser?.email}
            </p>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto" aria-label="Member navigation">
            {tabs.map((tab) => {
              const active = isActive(tab)
              const subs = tab.subItems ?? []
              return (
                <div key={tab.href}>
                  <Link
                    href={tab.href}
                    className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      active
                        ? "bg-kf-red/10 text-kf-red"
                        : "text-zinc-700 hover:bg-zinc-100"
                    }`}
                  >
                    <tab.Icon className="h-5 w-5" />
                    {tab.name}
                    {active && subs.length === 0 && (
                      <ChevronRight className="ml-auto h-4 w-4 shrink-0" aria-hidden />
                    )}
                  </Link>

                  {subs.length > 0 && active && (
                    <div className="mt-1 ml-7 mb-1 space-y-1 border-l border-zinc-200 pl-3">
                      {subs.map((sub) => {
                        const subActive = memberSubLinkIsActive(url, sub)
                        return (
                          <Link
                            key={sub.href}
                            href={sub.href}
                            className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors ${
                              subActive
                                ? "font-medium text-kf-red bg-kf-red/5"
                                : "text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
                            }`}
                            aria-current={subActive ? "page" : undefined}
                          >
                            <span className="flex-1">{sub.name}</span>
                          </Link>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </nav>

          {/* Portal links */}
          <PortalLinks currentUser={currentUser} />

          {/* Footer */}
          <div className="p-3 border-t border-zinc-200 space-y-1">
            <Link
              href="/account"
              className="flex w-full items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-zinc-700 hover:bg-zinc-100 transition-colors"
            >
              <Lock className="h-5 w-5" />
              Account
            </Link>
            <Link
              href="/logout"
              method="post"
              as="button"
              className="flex w-full items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-zinc-700 hover:bg-zinc-100 transition-colors"
            >
              <LogOut className="h-5 w-5" />
              Sign Out
            </Link>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        <main className="p-4 lg:p-6">
          <ViewAsMemberBanner />
          {children}
        </main>
      </div>
    </div>
  )
}

function MobileLayout({
  children,
  barTabs,
  moreTabs,
  homePlanningLinks,
  isActive,
  isMoreActive,
  url,
  currentUser,
  currentGroup,
  userGroups,
}) {
  const [moreOpen, setMoreOpen] = useState(false)

  const planningLinkIcon = (href) =>
    href.includes("balance_wheel") ? Compass : Home

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Top header */}
      <header className="fixed top-0 left-0 right-0 z-40 h-14 bg-white border-b border-zinc-200 flex items-center px-4">
        {/* Left: logo */}
        <Link href="/" className="shrink-0">
          <img src={logoBlackText} alt="Kingdom Factor" className="h-7" />
        </Link>

        {/* Center: group name / switcher */}
        <div className="flex-1 flex justify-center">
          {userGroups?.length > 1 ? (
            <GroupSwitcher
              currentGroup={currentGroup}
              userGroups={userGroups}
              className="max-w-[min(16rem,calc(100vw-5.5rem))]"
            />
          ) : (
            <span className="text-sm font-medium text-zinc-900 truncate max-w-[180px]">
              {currentGroup?.name}
            </span>
          )}
        </div>

        {/* Right: profile icon */}
        <ProfileSheet currentUser={currentUser} />
      </header>

      {/* Main content with padding for fixed header and tab bar */}
      <main className="pt-14 pb-20 px-4">
        <ViewAsMemberBanner />
        {children}
      </main>

      {/* Bottom tab bar */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-zinc-200"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        aria-label="Member navigation"
      >
        <div className="h-16 flex items-center justify-around">
          {barTabs.map((tab) => (
            <Link
              key={tab.href}
              href={tab.href}
              className={`flex flex-col items-center justify-center gap-0.5 flex-1 h-full text-xs font-medium transition-colors ${
                isActive(tab)
                  ? "text-kf-red"
                  : "text-zinc-500"
              }`}
              aria-current={isActive(tab) ? "page" : undefined}
            >
              <tab.Icon className="h-5 w-5" />
              <span>{tab.name}</span>
            </Link>
          ))}
          {moreTabs.length > 0 && (
            <button
              type="button"
              onClick={() => setMoreOpen(true)}
              className={`flex flex-col items-center justify-center gap-0.5 flex-1 h-full text-xs font-medium transition-colors ${
                isMoreActive ? "text-kf-red" : "text-zinc-500"
              }`}
            >
              <MoreHorizontal className="h-5 w-5" />
              <span>More</span>
            </button>
          )}
        </div>
      </nav>

      {/* More menu */}
      <ResponsiveDialog open={moreOpen} onOpenChange={setMoreOpen}>
        <ResponsiveDialogContent>
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle>More</ResponsiveDialogTitle>
          </ResponsiveDialogHeader>
          <div className="px-4 pb-4 space-y-1">
            {homePlanningLinks.length > 0 && (
              <div className="mb-3 space-y-1 border-b border-zinc-100 pb-3">
                <p className="px-3 text-xs font-medium uppercase tracking-wide text-zinc-400">
                  Home
                </p>
                {homePlanningLinks.map((sub) => {
                  const SubIcon = planningLinkIcon(sub.href)
                  const subActive = memberSubLinkIsActive(url, sub)
                  return (
                    <Link
                      key={sub.href}
                      href={sub.href}
                      onClick={() => setMoreOpen(false)}
                      className={`flex items-center gap-3 px-3 py-3 rounded-md text-sm font-medium transition-colors ${
                        subActive
                          ? "bg-kf-red/10 text-kf-red"
                          : "text-zinc-700 hover:bg-zinc-100"
                      }`}
                      aria-current={subActive ? "page" : undefined}
                    >
                      <SubIcon className="h-5 w-5 shrink-0" />
                      {sub.name}
                    </Link>
                  )
                })}
              </div>
            )}
            {moreTabs.map((tab) => (
              <Link
                key={tab.href}
                href={tab.href}
                onClick={() => setMoreOpen(false)}
                className={`flex items-center gap-3 px-3 py-3 rounded-md text-sm font-medium transition-colors ${
                  isActive(tab)
                    ? "bg-kf-red/10 text-kf-red"
                    : "text-zinc-700 hover:bg-zinc-100"
                }`}
              >
                <tab.Icon className="h-5 w-5" />
                {tab.name}
              </Link>
            ))}
          </div>
        </ResponsiveDialogContent>
      </ResponsiveDialog>
    </div>
  )
}

function ProfileSheet({ currentUser }) {
  const [open, setOpen] = useState(false)

  return (
    <ResponsiveDialog open={open} onOpenChange={setOpen}>
      <ResponsiveDialogTrigger asChild>
        <button className="shrink-0 p-1.5 rounded-full hover:bg-zinc-100 transition-colors">
          <User className="h-5 w-5 text-zinc-700" />
        </button>
      </ResponsiveDialogTrigger>
      <ResponsiveDialogContent>
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle>Account</ResponsiveDialogTitle>
        </ResponsiveDialogHeader>
        <div className="px-4 pb-4 space-y-4">
          <div>
            <p className="text-sm font-medium text-zinc-900">
              {currentUser?.name || "Member"}
            </p>
            <p className="text-xs text-zinc-500">{currentUser?.email}</p>
          </div>

          <Separator />

          <div className="space-y-1">
            {(currentUser?.isCoach || currentUser?.isAuthor) && (
              <Button asChild variant="ghost" className="w-full justify-start gap-3">
                <Link href="/app/coach">
                  <Users className="h-4 w-4" />
                  Coach Portal
                </Link>
              </Button>
            )}
            {currentUser?.isAdmin && (
              <Button asChild variant="ghost" className="w-full justify-start gap-3">
                <Link href="/app/admin">
                  <Shield className="h-4 w-4" />
                  Admin Portal
                </Link>
              </Button>
            )}
          </div>

          <Separator />

          <Button asChild variant="ghost" className="w-full justify-start gap-3 text-zinc-700">
            <Link href="/account">
              <Lock className="h-4 w-4" />
              Account
            </Link>
          </Button>

          <Button asChild variant="ghost" className="w-full justify-start gap-3 text-zinc-700">
            <Link href="/logout" method="post" as="button">
              <LogOut className="h-4 w-4" />
              Sign Out
            </Link>
          </Button>
        </div>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}

function PortalLinks({ currentUser }) {
  const hasLinks = currentUser?.isCoach || currentUser?.isAuthor || currentUser?.isAdmin

  if (!hasLinks) return null

  return (
    <>
      <Separator className="mx-3" />
      <div className="px-3 py-4 space-y-1">
        {(currentUser?.isCoach || currentUser?.isAuthor) && (
          <Link
            href="/app/coach"
            className="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-zinc-700 hover:bg-zinc-100 transition-colors"
          >
            <Users className="h-5 w-5" />
            Coach Portal
          </Link>
        )}
        {currentUser?.isAdmin && (
          <Link
            href="/app/admin"
            className="flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-zinc-700 hover:bg-zinc-100 transition-colors"
          >
            <Shield className="h-5 w-5" />
            Admin Portal
          </Link>
        )}
      </div>
    </>
  )
}
